import { ApiError } from './apiClient';

type SelfOnboardingErrorPayload = {
  code?: string;
  error?: string;
  message?: string;
  slug?: string;
  retryAttempt?: unknown;
  suggestions?: unknown;
};

export type ResolvedOnboardingError = {
  kind: 'conflict' | 'evaluation-disabled' | 'generic';
  message: string;
  slug?: string;
  retryAttempt?: number;
  suggestions: string[];
};

const normalizeRetryAttempt = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.floor(value);
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
};

const normalizeSuggestions = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const unique = new Set<string>();
  for (const item of value) {
    if (typeof item !== 'string') {
      continue;
    }

    const trimmed = item.trim();
    if (!trimmed) {
      continue;
    }

    unique.add(trimmed);
  }

  return Array.from(unique);
};

export const resolveOnboardingError = (error: unknown): ResolvedOnboardingError => {
  if (!(error instanceof ApiError)) {
    return {
      kind: 'generic',
      message: (error as { message?: string })?.message || 'Failed to create onboarding tenant.',
      suggestions: [],
    };
  }

  const details = (error.details ?? {}) as SelfOnboardingErrorPayload;
  const code = typeof details.code === 'string' ? details.code : '';
  const slug = typeof details.slug === 'string' ? details.slug : undefined;
  const retryAttempt = normalizeRetryAttempt(details.retryAttempt);
  const suggestions = normalizeSuggestions(details.suggestions);

  if (error.status === 409) {
    return {
      kind: 'conflict',
      message: details.error || 'That tenant slug is already in use. Please choose another slug.',
      slug,
      retryAttempt,
      suggestions,
    };
  }

  if (error.status === 403 && code === 'EVALUATION_ONBOARDING_DISABLED') {
    return {
      kind: 'evaluation-disabled',
      message: details.error || 'Evaluation onboarding is currently disabled. Contact support to request access.',
      retryAttempt,
      suggestions: [],
    };
  }

  return {
    kind: 'generic',
    message: details.error || details.message || error.message || 'Failed to create onboarding tenant.',
    suggestions: [],
  };
};
